import { Children, useMemo, cloneElement, useEffect } from 'react'

import { IProperty } from '../../../../types-library/src'
import { useMapStore, usePropertiesStore } from '../../../../state-library/src'

import EmptyState from './EmptyState'
import { composeClasses } from '@wiggot/utils/src/strings'
import SkeletonLoader from './SkeletonLoader'

interface Props {
  children?: React.ReactNode
  listClassName?: string
  skeletonClassName?: string
  flyToMapOnHoverCard?: boolean
  flyToMapPropertyId?: string
  onCleanSearch?: () => void
  onMouseEnter?: (property: IProperty) => void
  onMouseLeave?: (property: IProperty) => void
}

const PropertyList = ({
  children,
  listClassName,
  skeletonClassName,
  flyToMapOnHoverCard,
  flyToMapPropertyId,
  onCleanSearch,
  onMouseEnter,
  onMouseLeave,
}: Props): JSX.Element => {
  const { properties, propertyLoaderStatus, setHoveredPropertyCard } =
    usePropertiesStore()

  const { map } = useMapStore()

  const HeaderElement = useMemo(() => {
    return Children.toArray(children).find(
      (child: any) => child.type === PropertyList.Header
    )
  }, [children])

  const CardElement = useMemo(() => {
    return Children.toArray(children).find(
      (child: any) => child.type === PropertyList.Card
    )
  }, [children])

  const PaginatorElement = useMemo(() => {
    return Children.toArray(children).find(
      (child: any) => child.type === PropertyList.Paginator
    )
  }, [children])

  const LoaderElement = useMemo(() => {
    return Children.toArray(children).find(
      (child: any) => child.type === PropertyList.Loader
    )
  }, [children])

  const onHoverCard = (property: IProperty): void => {
    onMouseEnter?.(property)
    if (flyToMapOnHoverCard && map && property) {
      onFlyToMap(property)
    }
  }
  const onFlyToMap = (property: IProperty): void => {
    if (map && property) {
      const { latitude, longitude } = property

      map.flyTo({
        center: [longitude, latitude],
        duration: 300,
        zoom: 14.5,
      })

      setHoveredPropertyCard({
        id: property.id,
        latitude: property.latitude,
        longitude: property.longitude,
      })
    }
  }

  if (propertyLoaderStatus.loading && LoaderElement) {
    return <>{cloneElement(LoaderElement as React.ReactElement<LoaderProps>)}</>
  }

  if (propertyLoaderStatus.waitingAnimation) {
    return (
      <div>
        <SkeletonLoader
          className={listClassName}
          skeletonClassName={skeletonClassName}
        />
      </div>
    )
  }

  if (properties?.length === 0) {
    return (
      <div className="w-full flex flex-col justify-center ">
        {HeaderElement &&
          cloneElement(HeaderElement as React.ReactElement<HeaderProps>)}
        <EmptyState onCleanSearch={onCleanSearch} className="mt-[100px]" />
      </div>
    )
  }
  return (
    <>
      {HeaderElement &&
        cloneElement(HeaderElement as React.ReactElement<HeaderProps>)}
      <div
        className={composeClasses(
          'grid grid-cols-1 sm:grid-cols-2',
          listClassName
        )}
      >
        {CardElement &&
          properties.map((property) => {
            return cloneElement(CardElement as React.ReactElement<CardProps>, {
              property,
              key: property.id,
              onMouseEnter: onHoverCard,
              onMouseLeave: onMouseLeave,
              onFlyToMap,
              flyToMap: property.id === flyToMapPropertyId,
            })
          })}
      </div>

      {PaginatorElement &&
        cloneElement(PaginatorElement as React.ReactElement<PaginatorProps>)}
    </>
  )
}

export default PropertyList

interface HeaderProps {
  children: React.ReactNode
}

function Header({ children }: HeaderProps) {
  return cloneElement(children as React.ReactElement<CardProps>)
}

/**
 * React Node to render the property Card
 * */
PropertyList.Header = Header

interface CardProps {
  children: React.ReactNode
  property?: IProperty
  flyToMap?: boolean
  onMouseEnter?: (property: IProperty) => void
  onMouseLeave?: (property: IProperty) => void
  onFlyToMap?: (property: IProperty) => void
}

function Card({
  children,
  property,
  onMouseEnter,
  onMouseLeave,
  onFlyToMap,
  flyToMap,
}: CardProps) {
  return cloneElement(children as React.ReactElement<CardProps>, {
    property,
    onMouseEnter,
    onMouseLeave,
    onFlyToMap,
    flyToMap,
  })
}

/**
 * React Node to render the property Card
 * */
PropertyList.Card = Card

interface PaginatorProps {
  children: React.ReactNode
}

function Paginator({ children }: PaginatorProps) {
  return children
}

/**
 * React Node to render the property list Paginator
 * */
PropertyList.Paginator = Paginator

interface LoaderProps {
  children: React.ReactNode
}

function Loader({ children }: LoaderProps) {
  return children
}

/**
 * React Node to render the property list Loader
 * */
PropertyList.Loader = Loader
